// Reach Tool API client.
//
// Talks to the Orbit backend (backend/src/modules/reach_tool/), which owns configuration and
// enqueuing. The extraction itself runs in the batch-processing Function App, which this
// frontend never contacts — it has no public routes and holds the platform credentials. The
// browser asks for data and requests a job; it can never hold a credential.
//
// Server rows are bp_connectors/bp_queue shapes. `toUiStream` below maps them onto the object
// the components already expect, so the UI's vocabulary and the database's can each stay
// natural without either leaking into the other.

import { createApiClient } from '../../shared/http/createApiClient.js';

const client = createApiClient({ auth: true });

// bp_queue.status -> the badge vocabulary in ReachToolApp.
// 'queued' is deliberately its own label rather than folded into Processing: "waiting behind
// other work" and "running now" are different answers to "why is my data not here yet".
const RUN_STATUS = {
  succeeded: 'Successful',
  running: 'Processing',
  queued: 'Queued',
  // A suspended Durable orchestration: mid-flight, going nowhere until resumed. Not folded
  // into Queued — "waiting its turn" and "told to wait" are different answers.
  paused: 'Paused',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

// What each platform can extract, cached for the session: the registry is static per deploy,
// and the wizard + edit form both ask on every mount. A failed fetch is NOT cached, so a
// batch-app blip during one wizard open does not lock the error in for the session.
let _capabilitiesPromise = null;

function capabilities() {
  if (!_capabilitiesPromise) {
    _capabilitiesPromise = client.get('/reach-tool/capabilities')
      .catch(error => { _capabilitiesPromise = null; throw error; });
  }
  return _capabilitiesPromise;
}

function toUiStream(row) {
  const config = row.config || {};
  return {
    id: row.id,
    name: row.display_name || '',
    platform: row.platform,
    connectionId: row.connection_id || null,
    connectionLabel: row.connection_label || '',
    connectionStatus: row.connection_status || '',
    // The server stores one ad account per connector — the natural key is
    // (client, platform, account). The UI models it as an array, so wrap it.
    adAccounts: row.external_account_id ? [row.external_account_id] : [],
    accounts: config.accounts || [],
    email: config.email || '',
    reachType: config.reachType || 'weekly',
    cadence: config.cadence || 'weekly',
    time: config.time || '09:00',
    timezone: config.timezone || 'Asia/Beirut',
    weekdays: config.weekdays || [],
    monthDay: config.monthDay || 1,
    loadMode: config.loadMode || 'upsert',
    totalReplaceConfirmed: config.totalReplaceConfirmed === true,
    fieldProfile: config.fieldProfile,
    lookbackDays: config.lookbackDays,
    backfillFrom: config.backfillFrom,
    backfillTo: config.backfillTo,
    // What this stream extracts. The fallbacks mirror the server's own defaults for streams
    // created before the pickers existed, so editing one shows what it actually pulls.
    levels: config.levels ?? ['account'],
    kpis: config.kpis ?? ['impressions', 'reach', 'frequency', 'spend'],
    // Editable in the form since the beginning; persisted since now.
    owner: config.owner || '',
    tags: config.tags || [],
    enabled: row.enabled,

    runStatus: row.run_status ? (RUN_STATUS[row.run_status] || row.run_status) : 'Never run',
    lastRunDate: row.last_run_date || null,
    lastDataDate: row.last_data_date || null,

    // Schedule state, from the batch scheduler. nextRunAt null means the scheduler has not
    // computed this stream yet (it does within minutes of a create or edit) — the UI shows
    // "Scheduling…", never a guess. scheduleError is why the scheduler is skipping this
    // stream, when it is; empty when healthy.
    nextRunAt: row.next_run_at || null,
    scheduleError: row.schedule_error || '',

    // Live run detail, for progress and for an actionable failure message.
    queueId: row.last_queue_id || null,
    progressDone: row.progress_done || 0,
    progressTotal: row.progress_total || 0,
    lastError: row.last_error || '',
  };
}

// UI stream -> the request body the backend expects. Only the fields the server owns; run
// status and dates are derived server-side and are never sent back.
function toPayload(stream) {
  return {
    name: stream.name,
    createRequestId: stream.createRequestId,
    platform: stream.platform,
    // Which agency email this stream extracts with. Required on create — a stream without one
    // has no token and cannot run.
    connectionId: stream.connectionId || null,
    adAccounts: stream.adAccounts || [],
    accounts: stream.accounts || [],
    email: stream.email,
    reachType: stream.reachType,
    cadence: stream.cadence,
    time: stream.time,
    timezone: stream.timezone,
    weekdays: stream.weekdays,
    monthDay: stream.monthDay,
    loadMode: stream.loadMode,
    totalReplaceConfirmed: stream.totalReplaceConfirmed,
    fieldProfile: stream.fieldProfile,
    lookbackDays: stream.lookbackDays,
    // What to extract: reporting levels and metrics, from the wizard's Data step or the edit
    // form. Empty arrays mean all supported fields; absent keys retain legacy defaults.
    levels: stream.levels,
    kpis: stream.kpis,
    // Explicit initial extraction bounds. The server normalizes these to complete reporting
    // periods, so historical months are complete, but the current month/week may be partial.
    backfillFrom: stream.backfillFrom,
    backfillTo: stream.backfillTo,
    // Kept for older callers and already-saved configurations.
    backfillDays: stream.backfillDays,
    owner: stream.owner,
    tags: stream.tags,
    enabled: stream.enabled,
  };
}

// bp_pivots row <-> the pivot object the UI builds. The server stores the definition whole
// and never interprets it; these two functions are the only place the two shapes meet.
function toUiPivot(row) {
  return {
    ...(row.definition || {}),
    id: row.id,
    name: row.name,
    savedAt: row.updated_at,
  };
}

function toPivotPayload(pivot) {
  const { id, name, savedAt, ...definition } = pivot;
  return { name, definition };
}

// bp_calculated_fields row <-> the field object the formula engine and pivots use.
function toUiCalculatedField(row) {
  const definition = row.definition || {};
  return {
    id: row.id,
    name: row.name,
    type: row.field_type,
    formula: row.formula,
    description: definition.description || '',
    defaultAggregation: definition.defaultAggregation,
    calculated: true,
    updatedAt: row.updated_at,
  };
}

function toCalculatedFieldPayload(field) {
  return {
    name: field.name,
    field_type: field.type,
    formula: field.formula,
    definition: {
      description: field.description || '',
      defaultAggregation: field.defaultAggregation,
    },
  };
}

export const reachApi = {
  // --- capabilities ---
  // {platform: {levels: [...], kpis: [...]}} straight off the batch app's connector registry,
  // so the pickers can never drift from the mapping tables that actually extract.
  capabilities,

  // --- streams ---
  listStreams: async () => (await client.get('/reach-tool/streams')).map(toUiStream),

  createStream: async (stream) =>
    toUiStream(await client.post('/reach-tool/streams', toPayload(stream))),

  updateStream: async (id, stream) =>
    toUiStream(await client.patch(`/reach-tool/streams/${id}`, toPayload(stream))),

  deleteStream: (id) => client.del(`/reach-tool/streams/${id}`),

  // Force trigger / reprocess. Returns as soon as the job is queued — extraction can take up
  // to half an hour, so the caller polls listStreams (or the queue) for progress.
  //
  // `started_now: false` in the response is not a failure: it means the batch layer did not
  // acknowledge the nudge, so the job starts on the next 30-second sweep instead of instantly.
  runStream: (id, { from, to, ongoing = false, priority } = {}) =>
    client.post(`/reach-tool/streams/${id}/run`, { from, to, ongoing, priority }),

  history: (id) => client.get(`/reach-tool/streams/${id}/history`),

  // --- extracted data ---
  // What the extraction actually wrote, from bp_facts. The analysis screens (pivot tables,
  // exports, alerts) run on this. Everything here was extracted from a platform — there is no
  // generated data anywhere in this module.
  // `granularity` picks which basis the rows carry: 'day' for trends, 'week'/'month' for the
  // platform-de-duplicated period rows. Never mixed in one response — a weekly row IS its
  // days re-reported, so a combined set would double-count on the first sum.
  facts: ({ stream, streams, from, to, level = 'account', granularity = 'day' } = {}) =>
    client.get('/reach-tool/facts', { params: { stream, streams: streams?.join(','), from, to, level, granularity } }),
  factBounds: ({ streams = [], level = 'account', granularity = 'day' }) =>
    client.get('/reach-tool/facts', { params: { bounds: 'true', streams: streams.join(','), level, granularity } }),

  // --- queue ---
  // Returned in the exact order the batch dispatcher claims work, so this IS the execution
  // sequence rather than a plausible-looking approximation of it.
  listQueue: (status) => client.get('/reach-tool/queue', { params: { status } }),

  // Reordering affects queued work only. A 409 means the job already started or finished —
  // running work can be cancelled but not resequenced.
  setPriority: (queueId, priority) =>
    client.patch(`/reach-tool/queue/${queueId}/priority`, { priority }),

  // "Run this client's Facebook accounts ahead of everything else." One statement server-side.
  bulkPriority: (priority, platform = null) =>
    client.patch('/reach-tool/queue/bulk-priority', { priority, platform }),

  cancel: (queueId) => client.post(`/reach-tool/queue/${queueId}/cancel`, {}),

  // Suspend a RUNNING job mid-flight / wake it again. Cancel is for queued work; these are
  // for started work. Completed chunks stay written (idempotently) either way.
  pause: (queueId) => client.post(`/reach-tool/queue/${queueId}/pause`, {}),
  resume: (queueId) => client.post(`/reach-tool/queue/${queueId}/resume`, {}),

  runLog: (queueId) => client.get(`/reach-tool/queue/${queueId}/log`),

  // --- saved pivots & calculated fields ---
  // Server-side at last: a pivot saved at one desk opens at another, and clearing a browser
  // cache stops deleting a team's work. Ids are server UUIDs — a create returns the stored
  // row, and CALLERS MUST USE THE RETURNED ID, not the one they sent.
  listPivots: async () => (await client.get('/reach-tool/pivots')).map(toUiPivot),
  createPivot: async (pivot) => toUiPivot(await client.post('/reach-tool/pivots', toPivotPayload(pivot))),
  updatePivot: async (id, pivot) => toUiPivot(await client.patch(`/reach-tool/pivots/${id}`, toPivotPayload(pivot))),
  deletePivot: (id) => client.del(`/reach-tool/pivots/${id}`),

  listCalculatedFields: async () =>
    (await client.get('/reach-tool/calculated-fields')).map(toUiCalculatedField),
  createCalculatedField: async (field) =>
    toUiCalculatedField(await client.post('/reach-tool/calculated-fields', toCalculatedFieldPayload(field))),
  updateCalculatedField: async (id, field) =>
    toUiCalculatedField(await client.patch(`/reach-tool/calculated-fields/${id}`, toCalculatedFieldPayload(field))),
  deleteCalculatedField: (id) => client.del(`/reach-tool/calculated-fields/${id}`),

  // --- connections ---
  // A "connection" is one of the agency's ~7 email accounts on one platform — NOT a per-client
  // credential. Clients grant those emails access to their ad accounts, so one connection spans
  // many clients, and every route below is admin-only for that reason: the ad accounts a
  // connection can see belong to several clients at once.
  //
  // The token itself never reaches this app in either direction. These endpoints return which
  // email is connected and when it expires; the consent flow hands the code to the backend,
  // which forwards it to the batch layer.

  listConnections: (platform) =>
    client.get('/reach-tool/connections', { params: platform ? { platform } : {} }),

  // Connect one platform identity. Its app id, app secret and token travel together — there is
  // no global app for a lone token to pair with, which is why all three are required.
  //
  // `exchange` is Meta's short-lived-token option. Other platforms validate and store the token
  // form named by the connection UI (access or refresh) through the Reach auth registry.
  //
  // `label` is used only when the platform cannot report an email of its own, which is the case
  // for system-user tokens. Otherwise the platform's answer wins over anything typed here.
  // `connectionId` is present on a reconnect: it pins the update to the row the admin
  // clicked, so an email the platform reports differently than last time cannot quietly
  // create a second connection while the streams stay pointed at the dying one.
  connect: ({ appId, appSecret, token, platform = 'facebook', exchange = true, label = '', connectionId = '' }) =>
    client.post('/reach-tool/connections/token', {
      app_id: appId, app_secret: appSecret, token, platform, exchange, label,
      connection_id: connectionId,
    }),

  // Prove a stored credential still works, now — a dead token otherwise reads "Connected"
  // until an extraction happens to hit it. A working token on an expired row reactivates it.
  testConnection: (connectionId) =>
    client.post('/reach-tool/connections/test', { connection_id: connectionId }),

  // Undo a revoke. The row and token were kept precisely so this is possible; the server
  // validates the token before flipping the status, so "active" keeps meaning something.
  reactivateConnection: (connectionId) =>
    client.post('/reach-tool/connections/reactivate', { connection_id: connectionId }),

  // Remove a mistaken connection outright. Refused with the count while streams depend on
  // it — revoke is the tool for stopping a live one.
  deleteConnection: (connectionId) =>
    client.post('/reach-tool/connections/delete', { connection_id: connectionId }),

  // Every ad account one connection can see — across clients, which is why this is admin-only.
  // A 409 means that connection needs attention, not that the user is logged out.
  listAdAccounts: (connectionId) =>
    client.get('/reach-tool/connections/accounts', { params: { connection_id: connectionId } }),

  disconnect: (connectionId) =>
    client.post('/reach-tool/connections/disconnect', { connection_id: connectionId }),

  // Workspace-scoped BigQuery credentials. Secrets are submitted once and encrypted in
  // the batch service; GET responses contain only identity and destination metadata.
  listGcpConnections: () => client.get('/reach-tool/gcp/connections'),
  gcpConfiguration: () => client.get('/reach-tool/gcp/config'),
  connectGcpServiceAccount: (form) => client.post('/reach-tool/gcp/service-account', form),
  startGcpSignIn: (form) => client.post('/reach-tool/gcp/oauth/start', form),
  finishGcpSignIn: ({ code, state }) => client.post('/reach-tool/gcp/oauth/finish', { code, state }),
  gcpMetadata: (selection) => client.post('/reach-tool/gcp/metadata', selection),
  disconnectGcp: (connectionId) => client.post('/reach-tool/gcp/disconnect', { connectionId }),
  listExports: () => client.get('/reach-tool/gcp/exports'),
  exportHistory: () => client.get('/reach-tool/gcp/export-history'),
  exportHistoryPage: (params, signal) => client.get('/reach-tool/gcp/export-history', { params: { ...params, paged: 'true' }, signal }),
  saveExports: (definitions, exportId) => client.post('/reach-tool/gcp/exports', { definitions, exportId }),
  setExportEnabled: (exportId, enabled) => client.post('/reach-tool/gcp/export-state', { exportId, enabled }),
  deleteExport: (exportId) => client.post('/reach-tool/gcp/export-delete', { exportId }),
  runExport: (exportId) => client.post('/reach-tool/gcp/export-run', { exportId }),
  exportSource: (selection) => client.post('/reach-tool/gcp/export-source', selection),
  exportValues: (selection) => client.post('/reach-tool/gcp/export-values', selection),
  retryExportJobs: (exportId, jobIds) => client.post('/reach-tool/gcp/export-retry', { exportId, jobIds }),
};

export { toUiStream, RUN_STATUS };
